import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Upload, CheckCircle2 } from "lucide-react";
import { api, errText } from "../../lib/api";

export default function NewsletterPage() {
  const [d, setD] = useState(null);
  const [key, setKey] = useState("");
  const [listId, setListId] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/newsletter");
      setD(data);
      setListId(data.list_id || "");
    } catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // While an import runs, keep the progress line moving.
  useEffect(() => {
    if (d?.job?.status !== "running") return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [d?.job?.status, load]);

  const save = async (patch) => {
    setBusy("save"); setMsg(""); setErr("");
    try {
      const { data } = await api.put("/admin/newsletter", patch);
      setD(data); setListId(data.list_id || ""); setKey("");
      setMsg(patch.api_key ? "Key verified and saved." : "List saved — new buyers go there from now on.");
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(""); }
  };

  const importAll = async () => {
    setBusy("import"); setMsg(""); setErr("");
    try { await api.post("/admin/newsletter/import-all"); setMsg("Import started — it runs in the background."); await load(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(""); }
  };

  if (!d) return <p className="text-sm text-muted" data-testid="newsletter-loading">Loading…</p>;
  const job = d.job;

  return (
    <div className="space-y-5" data-testid="admin-newsletter-page">
      <div>
        <h1 className="font-display text-3xl text-ink">Newsletter · Acumbamail</h1>
        <p className="text-sm text-muted max-w-2xl">
          Buyers who tick the box at checkout are added to your Acumbamail list straight away — no confirmation
          e-mail, no waiting. Anyone who unticks it is remembered as a refusal and is never added, not even by
          the import below.
        </p>
      </div>

      {msg ? <p className="text-sm text-success inline-flex items-center gap-1.5" data-testid="newsletter-message">
        <CheckCircle2 size={14} /> {msg}</p> : null}
      {err ? <p className="text-sm text-danger" data-testid="newsletter-error">{err}</p> : null}

      <div className="card p-4 space-y-3" data-testid="newsletter-key-panel">
        <p className="smallcaps">API key</p>
        <p className="text-xs text-muted">
          Acumbamail → My account → Preferences / API. We check the key against your account before saving it.
          {d.configured ? ` Saved: ${d.api_key_masked}.` : " Nothing saved yet."}
        </p>
        <div className="flex flex-wrap gap-2">
          <input className="field flex-1 min-w-[260px]" type="password" autoComplete="new-password"
                 placeholder={d.configured ? d.api_key_masked : "Paste your Acumbamail auth token"}
                 value={key} data-testid="newsletter-key" onChange={(e) => setKey(e.target.value)} />
          <button className="btn-primary text-[11px] px-4 inline-flex items-center gap-2" disabled={!key || busy}
                  onClick={() => save({ api_key: key })} data-testid="newsletter-key-save">
            {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : null} Verify & save
          </button>
        </div>
      </div>

      <div className="card p-4 space-y-3" data-testid="newsletter-list-panel">
        <p className="smallcaps">List</p>
        {d.configured ? (
          <div className="flex flex-wrap gap-2 items-center">
            <select className="field flex-1 min-w-[260px]" value={listId} data-testid="newsletter-list"
                    onChange={(e) => setListId(e.target.value)}>
              <option value="">Choose a list…</option>
              {(d.lists || []).map((l) => (
                <option key={l.id} value={l.id}>{l.name} ({l.subscribers} subscribers)</option>
              ))}
            </select>
            <button className="btn-primary text-[11px] px-4" disabled={!listId || listId === d.list_id || busy}
                    onClick={() => save({ list_id: listId })} data-testid="newsletter-list-save">
              Save list
            </button>
            {d.ready ? <span className="text-xs text-success">Sending to {d.list_name}</span> : null}
          </div>
        ) : <p className="text-xs text-muted">Save a key first and your lists will appear here.</p>}
      </div>

      <div className="card p-4 space-y-3" data-testid="newsletter-import-panel">
        <p className="smallcaps">Past buyers</p>
        <p className="text-xs text-muted">
          One click sends every e-mail you already hold — orders, accounts and abandoned baskets — to the list,
          with no confirmation e-mail. Refusals are skipped automatically.
        </p>
        <div className="flex flex-wrap gap-4 items-center">
          <span className="text-sm text-ink tabular" data-testid="newsletter-candidates">
            {d.candidates} e-mail{d.candidates === 1 ? "" : "s"} ready
          </span>
          <span className="text-xs text-muted" data-testid="newsletter-counts">
            {d.counts.subscribed} on the list · {d.counts.suppressed} opted out
            {d.counts.failed ? ` · ${d.counts.failed} failed` : ""}
          </span>
          <div className="flex-1" />
          <button className="btn-primary text-[11px] px-4 inline-flex items-center gap-2"
                  disabled={!d.ready || busy || job?.status === "running"}
                  onClick={importAll} data-testid="newsletter-import">
            {busy === "import" || job?.status === "running"
              ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            Import all past buyers
          </button>
        </div>
        {job ? (
          <p className="text-xs text-muted" data-testid="newsletter-job">
            {job.status === "running" ? `Running… ${job.added}/${job.total} added`
              : job.status === "done" ? `Last import: ${job.added} added${job.failed ? `, ${job.failed} failed` : ""} · ${(job.finished_at || "").slice(0, 16).replace("T", " ")}`
                : `Last import failed: ${job.error}`}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// Reusable "put this buyer on the list" button for the order drawer and the customer profile.
export const AddToNewsletter = ({ email, testid = "add-to-newsletter", onDone }) => {
  const [state, setState] = useState("");
  const [error, setError] = useState("");
  const add = async () => {
    setState("busy"); setError("");
    try { await api.post("/admin/newsletter/subscribe", { email }); setState("done"); onDone?.(); }
    catch (e) { setState(""); setError(errText(e)); }
  };
  if (!email) return null;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <button className="btn-ghost text-[11px] inline-flex items-center gap-1.5" data-testid={testid}
              disabled={state === "busy" || state === "done"} onClick={add}
              title="Add this buyer to the Acumbamail list">
        {state === "busy" ? <Loader2 size={12} className="animate-spin" />
          : state === "done" ? <CheckCircle2 size={12} className="text-success" /> : <Mail size={12} />}
        {state === "done" ? "On the list" : "Add to newsletter"}
      </button>
      {error ? <span className="text-[10px] text-danger max-w-[220px]" data-testid={`${testid}-error`}>{error}</span> : null}
    </span>
  );
};
